import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useAuthStore } from '../../stores/authStore'

// WebSocket消息类型常量（与后端WebSocketMessageType枚举保持一致）
const WS_MESSAGE_TYPE = {
  MESSAGE: 'message',
  INTERMEDIATE_RESPONSE: 'intermediate_response',
  ERROR: 'error',
  SUBSCRIBE_ACK: 'subscribe_ack',
  UNSUBSCRIBE_ACK: 'unsubscribe_ack',
  TYPING_INDICATOR: 'typing_indicator',
  HANDSHAKE: 'handshake',
  HANDSHAKE_ACK: 'handshake_ack',
  HEARTBEAT: 'heartbeat',
  SUBSCRIBE_DOCUMENT_UPDATES: 'subscribe_document_updates',
  UNSUBSCRIBE_DOCUMENT_UPDATES: 'unsubscribe_document_updates',
  WORKFLOW_EVENT: 'workflow_event',
  RESPONSE: 'response',
} as const

export interface Message {
  id: string
  content: string
  sender: 'user' | 'bot'
  timestamp: Date
  status?: 'sending' | 'sent' | 'error'
  displayMode?: 'normal' | 'streaming' | 'typing'  // 显示模式：normal-普通消息，streaming-流式显示，typing-打字指示器
  isIntermediate?: boolean  // 是否为中间态响应（过渡回复）
}

export interface ConnectionStatus {
  isConnected: boolean
  isConnecting: boolean
  lastConnected: Date | null
  error: string | null
}

export interface ChatbotState {
  messages: Message[]
  connectionStatus: ConnectionStatus
  currentInput: string
  isLoading: boolean
  isStreaming: boolean
  websocket: WebSocket | null
  sessionId: string | null
  conversationId: string | null

  // Actions
  setCurrentInput: (input: string) => void
  addMessage: (message: Message) => void
  updateMessage: (id: string, updates: Partial<Message>) => void
  clearMessages: () => void
  sendMessage: (content: string) => Promise<void>
  connect: (welcomeText?: string) => Promise<void>
  disconnect: () => void
}

// 模块级连接锁，防止 React 重渲染导致的并发 connect()
let _connecting = false
// 心跳定时器
let _heartbeatTimer: ReturnType<typeof setInterval> | null = null
// StrictMode 防抖：延迟清锁，避免 unmount→remount 双连接
let _disconnectTimer: ReturnType<typeof setTimeout> | null = null

const initialState = {
  messages: [],
  connectionStatus: {
    isConnected: false,
    isConnecting: false,
    lastConnected: null,
    error: null,
  },
  currentInput: '',
  isLoading: false,
  isStreaming: false,
  websocket: null,
  sessionId: null,
  conversationId: null,
}

export const useChatbotStore = create<ChatbotState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setCurrentInput: (input) => {
        set({ currentInput: input })
      },

      addMessage: (message) => {
        set((state) => ({
          messages: [...state.messages, message],
        }))
      },

      updateMessage: (id, updates) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === id ? { ...msg, ...updates } : msg
          ),
        }))
      },

      clearMessages: () => {
        set({ messages: [] })
      },

      sendMessage: async (content) => {
        const { websocket, sessionId, conversationId } = get();
        if (!websocket || websocket.readyState !== WebSocket.OPEN) {
          // 如果WebSocket未连接，显示错误消息
          const errorMessage: Message = {
            id: Date.now().toString(),
            content: 'WebSocket连接未建立，请先连接',
            sender: 'bot',
            timestamp: new Date(),
          };
          set((state) => ({
            messages: [...state.messages, errorMessage],
            currentInput: '',
          }));
          return;
        }

        const userMessage: Message = {
          id: Date.now().toString(),
          content,
          sender: 'user',
          timestamp: new Date(),
          status: 'sending',
        }

        set((state) => ({
          messages: [...state.messages, userMessage],
          currentInput: '',
          isLoading: true,
        }))

        try {
          // 获取当前用户的公司ID
          const companyId = useAuthStore.getState().company?.id || null;

          // 通过WebSocket发送真实消息
           const messageData = {
             type: WS_MESSAGE_TYPE.MESSAGE,
             message: content,
             conversation_id: conversationId,
             session_id: sessionId,
             company_id: companyId,  // 新增：租户ID
             platform: "web",        // 平台类型
             platform_user_id: "test_user"  // 平台用户ID
           };
          websocket.send(JSON.stringify(messageData));

          // 将用户消息状态更新为已发送
          set((state) => ({
            messages: state.messages.map((msg) =>
              msg.id === userMessage.id ? { ...msg, status: 'sent' } : msg
            ),
          }));
        } catch (error) {
          console.error('发送消息失败:', error);
          set((state) => ({
            messages: state.messages.map((msg) =>
              msg.id === userMessage.id ? { ...msg, status: 'error' } : msg
            ),
            isLoading: false,
          }));
        }
      },

      connect: async (welcomeText?: string) => {
        // 取消待执行的 disconnect（StrictMode unmount→remount 场景）
        if (_disconnectTimer) { clearTimeout(_disconnectTimer); _disconnectTimer = null }
        // 同步锁：防止 React 重渲染导致并发创建多个 WebSocket
        if (_connecting) return
        const { websocket: existingWs } = get()
        if (existingWs && (existingWs.readyState === WebSocket.OPEN || existingWs.readyState === WebSocket.CONNECTING)) {
          return
        }
        _connecting = true

        set((state) => ({
          connectionStatus: {
            ...state.connectionStatus,
            isConnecting: true,
            error: null,
          },
        }))

        const generateId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const sessionId = generateId('session')
        // 从 localStorage 恢复，刷新后保持同一会话历史
        const storedConvId = localStorage.getItem('chatbot_conversation_id')
        const conversationId = storedConvId || generateId('conversation')
        if (!storedConvId) localStorage.setItem('chatbot_conversation_id', conversationId)

        const wsUrl = '/ws/chat/' + sessionId

        try {
          const ws = new WebSocket(wsUrl)

          ws.onopen = () => {
            _connecting = false
            set({
              connectionStatus: {
                isConnected: true,
                isConnecting: false,
                lastConnected: new Date(),
                error: null,
              },
              websocket: ws,
              sessionId,
              conversationId,
            })

            const companyId = useAuthStore.getState().company?.id || null;

            const handshakeData = {
              type: 'handshake',
              conversation_id: conversationId,
              session_id: sessionId,
              company_id: companyId,
              platform: "web",
              platform_user_id: "test_user"
            }
            ws.send(JSON.stringify(handshakeData))

            // 如果是恢复的会话，从后端拉取历史消息
            if (storedConvId) {
              fetch(`/api/v1/message/conversation/${conversationId}/messages`)
                .then(r => r.json())
                .then(data => {
                  const rawMsgs = Array.isArray(data) ? data : (data?.data?.messages || data?.messages || [])
                  if (rawMsgs.length > 0) {
                    const msgs: Message[] = rawMsgs.map((m: { message_id: string; direction: string; content?: { text?: string }; content_text?: string; timestamp: string }) => ({
                      id: m.message_id,
                      content: m.content?.text || m.content_text || '',
                      sender: m.direction === 'inbound' ? 'user' : 'bot',
                      timestamp: new Date(m.timestamp?.endsWith('Z') ? m.timestamp : m.timestamp + 'Z'),
                      status: 'sent' as const,
                    }))
                    set({ messages: msgs })
                  }
                })
                .catch(() => {/* 静默失败 */})
            }

            // 启动心跳（30秒间隔）
            if (_heartbeatTimer) clearInterval(_heartbeatTimer)
            _heartbeatTimer = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: WS_MESSAGE_TYPE.HEARTBEAT, timestamp: new Date().toISOString() }))
              }
            }, 30000)

            const welcomeMessage: Message = {
              id: Date.now().toString(),
              content: welcomeText || '',
              sender: 'bot',
              timestamp: new Date(),
            }
            set((state) => ({
              messages: [welcomeMessage],
            }))
          }

          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data)
              if (data.type === WS_MESSAGE_TYPE.INTERMEDIATE_RESPONSE && data.content) {
                const intermediateMessage: Message = {
                  id: `intermediate-${Date.now()}`,
                  content: data.content,
                  sender: data.sender === 'user' ? 'user' : 'bot',
                  timestamp: new Date(),
                  displayMode: data.display_mode || 'streaming',
                  isIntermediate: true,
                }
                set((state) => ({
                  messages: [...state.messages, intermediateMessage],
                }))
              } else if (data.type === WS_MESSAGE_TYPE.RESPONSE && data.response) {
                const botMessage: Message = {
                  id: Date.now().toString(),
                  content: data.response,
                  sender: data.sender === 'user' ? 'user' : 'bot',
                  timestamp: new Date(),
                  displayMode: 'normal',
                  isIntermediate: false,
                }
                set((state) => ({
                  messages: [...state.messages, botMessage],
                  isLoading: false,
                }))
              } else if (data.type === 'human_active_ack') {
                set({ isLoading: false })
              } else if (data.type === WS_MESSAGE_TYPE.ERROR) {
                set((state) => ({
                  connectionStatus: {
                    ...state.connectionStatus,
                    error: data.message || 'WebSocket错误',
                  },
                  isLoading: false,
                }))
              }
            } catch (error) {
              console.error('解析WebSocket消息失败:', error)
            }
          }

          ws.onerror = () => {
            _connecting = false
            if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null }
            set((state) => ({
              connectionStatus: {
                ...state.connectionStatus,
                isConnected: false,
                isConnecting: false,
                error: 'WebSocket连接错误',
              },
              websocket: null,
            }))
          }

          ws.onclose = () => {
            _connecting = false
            if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null }
            set((state) => ({
              connectionStatus: {
                ...state.connectionStatus,
                isConnected: false,
                isConnecting: false,
              },
              websocket: null,
            }))
          }

        } catch (error) {
          _connecting = false
          set((state) => ({
            connectionStatus: {
              ...state.connectionStatus,
              isConnected: false,
              isConnecting: false,
              error: '创建WebSocket连接失败',
            },
          }))
        }
      },

      disconnect: () => {
        // 延迟清理，避免 StrictMode unmount→remount 触发双连接
        if (_disconnectTimer) clearTimeout(_disconnectTimer)
        _disconnectTimer = setTimeout(() => {
          _disconnectTimer = null
          if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null }
          const { websocket } = get()
          if (websocket && websocket.readyState === WebSocket.OPEN) {
            websocket.close(1000, 'Manual disconnect')
          }
          _connecting = false
          set({
            connectionStatus: {
              isConnected: false,
              isConnecting: false,
              lastConnected: null,
              error: null,
            },
            websocket: null,
            messages: [],
          })
        }, 50)
      },
    }),
    { name: 'ChatbotStore' }
  )
)